import { takeLatest, put, select, call } from 'redux-saga/effects';
import { get } from 'lodash';
import update from 'immutability-helper';
import { dimensionsListSelector } from '@store/common/dimensionList/selectors';
import { createFilterRule, checkIfRuleAlreadyExist } from '@utils/filter';
import { getFilterFromRules, getRulesFromFilter } from '@utils';
import { getSelectedKPI } from '@src/api/SPI';
import * as actions from './actions';
import * as types from './types';
import * as selectors from './selectors';
import { filterPersistedData } from './utils';
import * as spiActions from '../spiThresholds/actions';
import { fetchSPIThresholds } from '@store/overviewDashboard/spiThresholds/actions';
import { DEFAULT_KPI_CONFIG, WIDGET_TYPES } from '@src/utils/kpi';

function* initRequest() {
  yield put(actions.init());
}

function* initBase(action) {
  // persistence action is common
  // ignore if triggered from another tab
  const key = get(action, ['response', 'key'], null);
  const { meta = {} } = action;

  if (key !== '/ovb') {
    return;
  }
  const persistedOvb = get(action, ['response', 'configuration', 'ovb'], null);
  const filteredState = filterPersistedData(persistedOvb);
  const dimensionList = yield select(dimensionsListSelector);
  const groupByList = dimensionList.map((d) => d.groupBy);
  // Replace the deleted dimensions with other dimensions from dimension list
  const widgets = get(filteredState, 'widgets');
  widgets.forEach((widget) => {
    if (widget.type === WIDGET_TYPES.dimensionTable) {
      const dimensions = widget.config.dimensions;
      const filteredDimensions = dimensions.filter((d) => groupByList.includes(d));
      const removedElementsNumber = dimensions.length - filteredDimensions.length;
      const groubyListElementNotInDimensions = groupByList.filter(
        (d) => !filteredDimensions.includes(d),
      );
      // Adding dimensions from groupByList to ensure there are same number of dimensions in widget config
      for (let i = 0; i < removedElementsNumber; i++) {
        filteredDimensions.push(groubyListElementNotInDimensions[i]);
      }
      widget.config.dimensions = filteredDimensions;
    }
  });
  yield put(actions.updateBase(filteredState));
  yield put(actions.fetchSelectedKPI());
  yield put(fetchSPIThresholds());

  if ('doneCallback' in meta) {
    meta.doneCallback();
  }
}

// trigger action for persistence saga
// for all the relevant events in overview dashboard
function* triggerPersistence() {
  const baseState = yield select(selectors.dataToPersistSelector);
  yield put(actions.persistState({ ovb: baseState }));
}

function* dimensionDrillown(action) {
  const { dimension, row } = action;

  // get current dimesion and filter
  const dimensionList = yield select(dimensionsListSelector);
  const filter = yield select(selectors.filterSelector);
  const dimensionObj = dimensionList.find((d) => d.groupBy === dimension);

  // create drill down rule
  const drilldownRule = createFilterRule(
    dimensionObj,
    get(row, 'name[0]', ''),
    get(row, 'value[0]', ''),
  );
  // check if the drill down rule already exists in the applied filter
  const doesRuleExist = checkIfRuleAlreadyExist(filter.rule, drilldownRule);

  // add drilldown rule to current filter rules if they don't already exist
  if (!doesRuleExist) {
    const combinedFilter = update(filter.rule, {
      $push: [[drilldownRule]],
    });

    // add to breadcrumb
    // TODO: is formatting required?
    const crumbRules = getRulesFromFilter([[drilldownRule]], true);
    const crumbFilter = getFilterFromRules(crumbRules, true);
    yield put(actions.pushBreadcrumb(crumbFilter));

    // parse and apply
    const newRules = getRulesFromFilter(combinedFilter, true);
    const newFilter = getFilterFromRules(newRules, true);
    yield put(actions.applyFilter(newFilter));
  }
}

function* applyBreadcrumbFilter(action) {
  const { breadcrumb, index } = action;
  const combinedFilter: any[] = [];
  breadcrumb.slice(1, index + 1).forEach((item) => {
    if (Array.isArray(item.rule[0])) {
      item.rule.forEach((r) => {
        combinedFilter.push(r);
      });
    } else {
      combinedFilter.push(item.rule);
    }
  });
  const newRules = getRulesFromFilter(combinedFilter, true);
  const newFilter = getFilterFromRules(newRules, true);
  yield put(actions.applyFilter(newFilter));
}

function* getSelectedKPIConfig() {
  try {
    const response = yield call(getSelectedKPI, {});
    const { data } = response;
    yield put(actions.setSelectedKPIId(data));
    yield put(spiActions.setSelectedKPIConfig(data));
  } catch (error) {
    yield put(actions.setSelectedKPIId(DEFAULT_KPI_CONFIG));
  }
}

function* watchInitRequest() {
  yield takeLatest(types.INIT, initRequest);
}

function* watchDimensionDrilldown() {
  yield takeLatest(types.DIMENSION_DRILLDOWN, dimensionDrillown);
}

function* watchBreadcrumb() {
  yield takeLatest(types.POP_BREADCRUMB, applyBreadcrumbFilter);
}

function* watchInitSelectedKPIConfig() {
  yield takeLatest(types.FETCH_SELECTED_KPI_CONFIG, getSelectedKPIConfig);
}

function* watchPersistedState() {
  yield takeLatest([types.UPDATE_PERSISTED_OVERVIEW_STATE], initBase);
}

// actions to watch for persistence
function* watchActions() {
  yield takeLatest(
    [
      types.APPLY_FILTER,
      types.SET_SELECTED_DIMENSION,
      types.SWAP_SELECTED_DIMENSION,
      types.SET_SELECTED_KPI,
    ],
    triggerPersistence,
  );
}

export default [
  watchInitRequest,
  watchPersistedState,
  watchActions,
  watchDimensionDrilldown,
  watchBreadcrumb,
  watchInitSelectedKPIConfig,
];
